/**
 * Inicio de sesion.
 *
 * SSO de Google Workspace: el boton de Google entrega un ID token que se guarda
 * en el `token-store` y viaja en `Authorization: Bearer <id_token>` a la API. La
 * UI no valida nada del token — quien decide si ese correo entra, y con que rol,
 * es la API.
 *
 * En `VITE_AUTH_MODE=dev` se ofrece un acceso local sin Google para poder
 * levantar la UI contra un backend de desarrollo. La API solo acepta esos tokens
 * cuando ella misma esta en modo dev.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { readEnvironment } from '../app/environment';
import { useTheme } from '../app/theme';
import { useAuth } from '../auth/auth-context';
import { loadGoogleIdentity } from '../auth/google';
import type { GoogleIdentityApi } from '../auth/google';
import { EnvironmentBadge } from '../components/EnvironmentBadge';
import { ErrorBanner } from '../components/ErrorBanner';
import { MiddleMark } from '../components/MiddleLogo';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { useDocumentTitle } from '../hooks/use-document-title';

const DEFAULT_ROUTE = '/mensajes';

/** Ruta a la que volver despues del login, si venia de una pantalla privada. */
function readRedirectTarget(state: unknown): string {
  if (typeof state !== 'object' || state === null) {
    return DEFAULT_ROUTE;
  }
  const candidate = (state as { readonly from?: unknown }).from;
  return typeof candidate === 'string' && candidate.startsWith('/') ? candidate : DEFAULT_ROUTE;
}

export function LoginPage(): ReactElement {
  const environment = readEnvironment();
  const { status, error, signIn } = useAuth();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [google, setGoogle] = useState<GoogleIdentityApi | null>(null);
  const [googleError, setGoogleError] = useState<unknown>(null);
  const [devEmail, setDevEmail] = useState('');

  const { authMode, googleClientId, googleHostedDomain } = environment;
  const isGoogle = authMode === 'google';

  useDocumentTitle('Iniciar sesión');

  useEffect(() => {
    if (!isGoogle || googleClientId === '') {
      return;
    }
    let cancelled = false;
    loadGoogleIdentity().then(
      (api) => {
        if (cancelled) {
          return;
        }
        api.initialize({
          client_id: googleClientId,
          callback: (response) => {
            if (typeof response.credential === 'string' && response.credential !== '') {
              signIn(response.credential);
              return;
            }
            setGoogleError(new Error('Google no devolvió un ID token. Reintenta el acceso.'));
          },
          ...(googleHostedDomain === '' ? {} : { hosted_domain: googleHostedDomain }),
        });
        setGoogle(api);
      },
      (loadError: unknown) => {
        if (!cancelled) {
          setGoogleError(loadError);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [isGoogle, googleClientId, googleHostedDomain, signIn]);

  // El boton de Google es un iframe con colores propios: se vuelve a dibujar
  // cuando cambia el tema, para que no quede un boton claro sobre fondo oscuro.
  useEffect(() => {
    const container = buttonRef.current;
    if (google === null || container === null) {
      return;
    }
    container.replaceChildren();
    google.renderButton(container, {
      type: 'standard',
      theme: resolvedTheme === 'dark' ? 'filled_black' : 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      locale: 'es',
    });
  }, [google, resolvedTheme]);

  if (status === 'authenticated') {
    return <Navigate to={readRedirectTarget(location.state)} replace />;
  }

  return (
    <div className="login">
      <main className="login__card" aria-labelledby="login-title">
        <div className="login__brand">
          <MiddleMark size={44} />
          <div>
            <h1 id="login-title" className="login__title">
              Middle
            </h1>
            <p className="login__tagline">Monitor de integraciones · Grupo Amoblé</p>
          </div>
        </div>

        <p className="login__intro">
          Herramienta interna de diagnóstico del middleware. El acceso está restringido a las
          cuentas autorizadas de Grupo Amoblé.
        </p>

        <ErrorBanner error={googleError ?? error} />

        {isGoogle ? (
          googleClientId === '' ? (
            <div className="banner banner--warning">
              Falta configurar <code>VITE_GOOGLE_CLIENT_ID</code> en el <code>.env</code> de la UI:
              sin el client ID no se puede mostrar el acceso con Google.
            </div>
          ) : (
            <div className="login__google" ref={buttonRef} />
          )
        ) : (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              const email = devEmail.trim();
              if (email !== '') {
                // Token de desarrollo: la API lo acepta solo con AUTH_MODE=dev.
                signIn(`dev:${email}`);
              }
            }}
          >
            <div className="banner banner--warning">
              Modo de desarrollo: el acceso no pasa por Google. Nunca debe quedar activo en
              producción.
            </div>
            <label className="field field--wide">
              <span className="field__label">Correo</span>
              <input
                className="input"
                type="email"
                value={devEmail}
                placeholder="nombre@grupoamoble.com"
                autoComplete="email"
                onChange={(event) => setDevEmail(event.target.value)}
              />
            </label>
            <button type="submit" className="button button--primary" disabled={devEmail === ''}>
              Entrar
            </button>
          </form>
        )}

        {status === 'checking' ? <p className="muted">Verificando la sesión…</p> : null}

        <footer className="login__footer">
          <EnvironmentBadge label={environment.environmentLabel} />
          <ThemeSwitcher iconsOnly />
        </footer>
      </main>
    </div>
  );
}
